import { useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { useTranslation } from "../../i18n";

const kicker = "text-[.78rem] font-bold uppercase tracking-[.16em] text-juhuu";
const leadClass =
  "text-[clamp(1.05rem,0.7vw+0.95rem,1.2rem)] font-medium leading-[1.65] text-muted";
const chipClass =
  "inline-flex items-center rounded-full border border-line-strong bg-white px-[.85rem] py-[.4rem] text-[.82rem] font-semibold text-muted transition-colors hover:border-juhuu hover:text-juhuu";

const Check = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    className="mt-px h-5 w-5 flex-shrink-0 text-juhuu"
  >
    <path d="m5 12 5 5L20 7" />
  </svg>
);

const CUSTOM_TILE_ICONS = [
  <path d="M7 3v18M7 8h10l-1.5 6H7" />,
  <>
    <rect x="4" y="3" width="16" height="18" rx="2" />
    <path d="M9 7h6M9 11h6M9 15h3" />
  </>,
  <>
    <path d="M3 18 21 6M3 6l18 12" />
    <circle cx="12" cy="12" r="2" />
  </>,
];

export default function NewIndustriesRoute() {
  const { t } = useTranslation();
  const { hash } = useLocation();

  // Honour #parking / #rental / #custom on load and on hash change
  useEffect(() => {
    if (!hash) return;
    const el = document.querySelector(hash);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [hash]);

  const bullets = (base: string) => (
    <div className="flex flex-col gap-3">
      {[0, 1, 2].map((i) => (
        <div key={i} className="flex items-start gap-2.5">
          <Check />
          <span className="text-[.95rem] text-ink-2">{t(`${base}.${i}`)}</span>
        </div>
      ))}
    </div>
  );

  const statCard = (
    value: string,
    label: string,
    note: string,
    tinted: boolean
  ) => (
    <div
      className={`rounded-2xl border border-line p-[clamp(1.7rem,3vw,2.6rem)] shadow-sm ${
        tinted ? "bg-bg-alt" : "bg-white"
      }`}
    >
      <div className="text-[clamp(2.1rem,3.4vw,3rem)] font-extrabold leading-none tracking-[-0.04em] text-juhuu">
        {value}
      </div>
      <div className="mt-2 text-[.78rem] font-bold uppercase tracking-[.1em] text-light">
        {label}
      </div>
      <hr className="my-6 h-px border-0 bg-line" />
      <p className="text-[.92rem] leading-[1.6] text-muted">{note}</p>
    </div>
  );

  return (
    <div className="min-h-screen overflow-x-hidden bg-white">
      <main>
        {/* ── Page hero ────────────────────────────────────── */}
        <section className="relative px-4 md:px-8 pt-32 pb-[clamp(2.5rem,5vw,4rem)] text-center">
          <div className="pointer-events-none absolute -top-28 left-1/2 h-[440px] w-[440px] -translate-x-1/2 rounded-full bg-juhuu-glow blur-[90px]" />
          <div className="relative mx-auto max-w-7xl">
            <p className={kicker}>{t("routes.industries.kicker")}</p>
            <h1 className="mt-4 text-[clamp(2.4rem,4.6vw,3.8rem)] font-extrabold leading-[1.02] tracking-[-0.035em] text-ink">
              {t("routes.industries.heroTitleLine1")}
              <br />
              {t("routes.industries.heroTitleLine2")}
            </h1>
            <p className={`mx-auto mt-5 max-w-[620px] ${leadClass}`}>
              {t("routes.industries.heroDescription")}
            </p>
          </div>
        </section>

        {/* ── Quick nav chips ──────────────────────────────── */}
        <section className="px-4 md:px-8 pb-14">
          <div className="mx-auto flex max-w-7xl flex-wrap justify-center gap-2.5">
            <a className={chipClass} href="#parking">
              {t("routes.industries.chipParking")}
            </a>
            <a className={chipClass} href="#rental">
              {t("routes.industries.chipRental")}
            </a>
            <a className={chipClass} href="#custom">
              {t("routes.industries.chipCustom")}
            </a>
          </div>
        </section>

        {/* ── Bike parking ─────────────────────────────────── */}
        <section
          id="parking"
          className="scroll-mt-[90px] px-4 md:px-8 py-20"
        >
          <div className="mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-2">
            <div>
              <div className="mb-5 grid h-[54px] w-[54px] place-items-center rounded-[13px] border border-juhuu-soft-2 bg-juhuu-soft text-juhuu">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  className="h-[22px] w-[22px]"
                >
                  <rect x="3" y="7" width="18" height="13" rx="2" />
                  <path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                </svg>
              </div>
              <h2 className="text-[clamp(1.6rem,2.5vw,2.3rem)] font-extrabold leading-[1.08] tracking-[-0.03em] text-ink">
                {t("routes.industries.parkingTitle")}
              </h2>
              <p className="my-5 text-base leading-[1.65] text-muted">
                {t("routes.industries.parkingDescription")}
              </p>
              {bullets("routes.industries.parkingBullets")}
            </div>
            {statCard(
              t("routes.industries.parkingStatValue"),
              t("routes.industries.parkingStatLabel"),
              t("routes.industries.parkingStatNote"),
              true
            )}
          </div>
        </section>

        {/* ── Bike Rental (order-flip) ─────────────────────── */}
        <section
          id="rental"
          className="scroll-mt-[90px] bg-bg-alt px-4 md:px-8 py-20"
        >
          <div className="mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-2">
            <div className="order-2 lg:order-1">
              {statCard(
                t("routes.industries.rentalStatValue"),
                t("routes.industries.rentalStatLabel"),
                t("routes.industries.rentalStatNote"),
                false
              )}
            </div>
            <div className="order-1 lg:order-2">
              <div className="mb-5 grid h-[54px] w-[54px] place-items-center rounded-[13px] border border-juhuu-soft-2 bg-juhuu-soft text-juhuu">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  className="h-[22px] w-[22px]"
                >
                  <circle cx="6" cy="17" r="3" />
                  <circle cx="18" cy="17" r="3" />
                  <path d="M6 17 10 9h4m1 8-4-8m0 0 2-3h3" />
                </svg>
              </div>
              <h2 className="text-[clamp(1.6rem,2.5vw,2.3rem)] font-extrabold leading-[1.08] tracking-[-0.03em] text-ink">
                {t("routes.industries.rentalTitle")}
              </h2>
              <p className="my-5 text-base leading-[1.65] text-muted">
                {t("routes.industries.rentalDescription")}
              </p>
              {bullets("routes.industries.rentalBullets")}
            </div>
          </div>
        </section>

        {/* ── Other use cases (dark card) ──────────────────── */}
        <section id="custom" className="scroll-mt-[90px] px-4 md:px-8 py-20">
          <div className="mx-auto max-w-7xl">
            <div className="relative overflow-hidden rounded-[28px] bg-ink p-[clamp(2rem,5vw,4rem)]">
              <div className="pointer-events-none absolute -top-28 -right-20 h-[420px] w-[420px] rounded-full bg-juhuu/30 blur-[90px]" />
              <div className="relative grid items-center gap-[clamp(2rem,4vw,4rem)] lg:grid-cols-[1.1fr_1fr]">
                <div>
                  <div className="inline-flex items-center gap-2 rounded-full border border-white/[.16] bg-white/[.08] px-3.5 py-1.5 text-sm font-bold text-white">
                    <span className="h-[7px] w-[7px] rounded-full bg-juhuu" />
                    {t("routes.industries.customBadge")}
                  </div>
                  <h2 className="mt-5 text-[clamp(1.7rem,2.8vw,2.6rem)] font-extrabold leading-[1.08] tracking-[-0.03em] text-white">
                    {t("routes.industries.customTitleLine1")}
                    <br />
                    {t("routes.industries.customTitleLine2")}
                  </h2>
                  <p className="my-6 max-w-[480px] leading-[1.7] text-white/[.62]">
                    {t("routes.industries.customDescription")}
                  </p>
                  <Link
                    to="/meeting"
                    className="inline-flex items-center rounded-full bg-white px-7 py-3.5 font-semibold text-ink transition-all hover:-translate-y-0.5 hover:shadow-lg"
                  >
                    {t("routes.industries.customCta")}
                  </Link>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  {[0, 1, 2].map((i) => (
                    <div
                      key={i}
                      className="rounded-[14px] border border-white/10 bg-white/[.05] p-[1.1rem]"
                    >
                      <div className="mb-2.5 text-juhuu">
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          className="h-[22px] w-[22px]"
                        >
                          {CUSTOM_TILE_ICONS[i]}
                        </svg>
                      </div>
                      <div className="text-[.92rem] font-bold text-white">
                        {t(`routes.industries.customTiles.${i}`)}
                      </div>
                    </div>
                  ))}

                  <div className="flex flex-col justify-center rounded-[14px] border border-white/[.15] bg-gradient-to-br from-juhuu to-[#8b46ff] p-[1.1rem]">
                    <div className="mb-2 text-white">
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        className="h-6 w-6"
                      >
                        <path d="M12 5v14M5 12h14" />
                      </svg>
                    </div>
                    <div className="text-[.92rem] font-extrabold text-white">
                      {t("routes.industries.customTiles.3")}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Closing CTA ──────────────────────────────────── */}
        <section className="bg-bg-tint px-4 md:px-8 py-20 text-center">
          <div className="mx-auto max-w-[620px]">
            <h2 className="text-[clamp(1.8rem,3vw,2.6rem)] font-extrabold leading-[1.08] tracking-[-0.03em] text-ink">
              {t("routes.industries.finalCtaTitle")}
            </h2>
            <p className={`mx-auto my-6 max-w-[460px] ${leadClass}`}>
              {t("routes.industries.finalCtaDescription")}
            </p>
            <Link
              to="/meeting"
              className="inline-flex items-center rounded-full bg-juhuu px-7 py-3.5 font-semibold text-white shadow-[0_14px_30px_-10px_rgba(109,22,250,.55)] transition-all hover:-translate-y-0.5 hover:bg-juhuu-ink"
            >
              {t("routes.industries.finalCtaButton")}
            </Link>
          </div>
        </section>
      </main>
    </div>
  );
}
